// app/context/cart-context.tsx
import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

import { cartFetch } from '~/utils/cart-queue';
interface CartContextType {
  isCartOpen: boolean;
  cartCount: number;
  openCart: () => void;
  closeCart: () => void;
  toggleCart: () => void;
  incrementCount: (amount: number) => void;
  decrementCount: (amount: number) => void;
  setCartCount: (count: number) => void;
  refreshCartCount: () => Promise<void>;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export function CartProvider({ children }: { children: ReactNode }) {
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [cartCount, setCartCount] = useState(0);

  // Initialize cart count on mount
  useEffect(() => {
    refreshCartCount();
  }, []);

  // Refresh cart count from API
  const refreshCartCount = async (): Promise<void> => {
    try {
      const selectedStore = localStorage.getItem('selectedStore');
      const order_id = selectedStore
        ? localStorage.getItem(`token_${selectedStore}`) || 'default_order_id'
        : 'default_order_id';

      const body = {
        actionType: 'GET',
        filter: { order_id: { eq: order_id } },
      };

      const response = await cartFetch('/api/cart', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (response.ok) {
        const data = await response.json();
        // Safety check: ensure data.edges exists before calling reduce
        const count =
          data.edges?.reduce(
            (sum: number, edge: any) => sum + (parseInt(edge.node.quantity, 10) || 0),
            0,
          ) || 0;
        setCartCount(count);
      }
    } catch (error) {
      console.error('Error fetching cart count:', error);
    }
  };

  const openCart = () => setIsCartOpen(true);
  const closeCart = () => setIsCartOpen(false);
  const toggleCart = () => setIsCartOpen(!isCartOpen);

  // Methods to modify cart count without API calls
  const incrementCount = (amount: number = 1) => {
    setCartCount((prev) => prev + amount);
  };

  const decrementCount = (amount: number = 1) => {
    setCartCount((prev) => Math.max(0, prev - amount));
  };

  return (
    <CartContext.Provider
      value={{
        isCartOpen,
        cartCount,
        openCart,
        closeCart,
        toggleCart,
        incrementCount,
        decrementCount,
        setCartCount,
        refreshCartCount,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}
